import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Globe, Image, Loader2, Monitor, Save, ShieldAlert, Sparkles, Smartphone } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ThemePatternBackground } from '@/components/ThemePatternBackground';
import {
  THEME_PALETTE_OPTIONS,
  VISUAL_PATTERN_OPTIONS,
  resolveThemePalette,
  type ThemePaletteId,
  type VisualPatternId,
} from '@/lib/portal-theme';

type StorefrontIndustryTemplate =
  | 'haulier'
  | 'transport'
  | 'warehouse'
  | 'freight_forwarder'
  | 'depot'
  | 'chemical_logistics'
  | 'cold_chain'
  | 'project_cargo'
  | 'total_logistics';

type StorefrontTone = 'corporate' | 'friendly' | 'premium' | 'operations_first';
type StorefrontMediaStrategy = 'tenant_upload' | 'industry_fallback' | 'generated_illustration';

interface StorefrontVisualProfile {
  industryTemplate: StorefrontIndustryTemplate;
  tone: StorefrontTone;
  mediaStrategy: StorefrontMediaStrategy;
  heroMediaUrl: string;
  heroMediaAlt: string;
  visualPattern: VisualPatternId;
  palette: ThemePaletteId;
}

interface StorefrontLitePayload {
  enabled: boolean;
  siteId: string | null;
  storefrontHost: string;
  storefrontUrl: string;
  portalHost: string;
  portalUrl: string;
  site: {
    brandName: string;
    brandShortName: string;
    legalName: string;
    tagline: string;
    portalLabel: string;
    footerBlurb: string;
  };
  home: {
    heroBadge: string;
    heroTitle: string;
    heroAccent: string;
    heroDescription: string;
    ctaTitle: string;
    ctaDescription: string;
  };
  pages: {
    services: { enabled: boolean; title: string; intro: string };
    contact: { enabled: boolean; title: string; intro: string };
  };
  modules: {
    tracking: boolean;
    sailingSchedule: boolean;
    bookings: boolean;
    salesInquiry: boolean;
    otherInquiry: boolean;
    portalLogin: boolean;
    aiChat: boolean;
  };
  visual: StorefrontVisualProfile;
}

type FormState = Omit<StorefrontLitePayload, 'enabled' | 'siteId'> & { siteId: string | null };
type StorefrontDraftResponse = Pick<FormState, 'site' | 'home'>;

const INDUSTRY_OPTIONS: Array<{ value: StorefrontIndustryTemplate; label: string; hint: string }> = [
  { value: 'haulier', label: 'Container Haulier', hint: 'Prime mover, container yard, port gate and delivery order workflow.' },
  { value: 'transport', label: 'Local Transport', hint: 'Van, route, POD, driver dispatch and delivery windows.' },
  { value: 'warehouse', label: 'Warehouse / 3PL', hint: 'Warehouse, pallets, receiving, picking, inventory and fulfilment.' },
  { value: 'freight_forwarder', label: 'Freight Forwarder', hint: 'Ocean, air, documentation, customs and booking coordination.' },
  { value: 'depot', label: 'Depot / Yard', hint: 'Container gate, inspection, repair, release and yard status.' },
  { value: 'chemical_logistics', label: 'Chemical / ISO Tank', hint: 'ISO tanks, DG safety, compliance and controlled movement.' },
  { value: 'cold_chain', label: 'Cold Chain', hint: 'Reefer, temperature handling, cold room and delivery traceability.' },
  { value: 'project_cargo', label: 'Project Cargo', hint: 'Oversized cargo, crane, site delivery and special handling.' },
  { value: 'total_logistics', label: 'Total Logistics', hint: 'Control tower for multi-service logistics groups.' },
];

const TONE_OPTIONS: Array<{ value: StorefrontTone; label: string }> = [
  { value: 'corporate', label: 'Corporate' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'premium', label: 'Premium' },
  { value: 'operations_first', label: 'Operations-first' },
];

const MEDIA_STRATEGY_OPTIONS: Array<{ value: StorefrontMediaStrategy; label: string }> = [
  { value: 'tenant_upload', label: 'Use company image URL' },
  { value: 'industry_fallback', label: 'Use Boox industry fallback' },
  { value: 'generated_illustration', label: 'Use generated illustration style' },
];

const DEFAULT_VISUAL_PROFILE: StorefrontVisualProfile = {
  industryTemplate: 'freight_forwarder',
  tone: 'corporate',
  mediaStrategy: 'industry_fallback',
  heroMediaUrl: '',
  heroMediaAlt: '',
  visualPattern: 'route_atlas',
  palette: 'ocean',
};

const INDUSTRY_VISUAL_DEFAULTS: Record<StorefrontIndustryTemplate, Pick<StorefrontVisualProfile, 'palette' | 'visualPattern'>> = {
  haulier: { palette: 'amber', visualPattern: 'container_haulage' },
  transport: { palette: 'signal_blue', visualPattern: 'route_atlas' },
  warehouse: { palette: 'emerald', visualPattern: 'warehouse_grid' },
  freight_forwarder: { palette: 'ocean', visualPattern: 'route_atlas' },
  depot: { palette: 'amber', visualPattern: 'depot_gate' },
  chemical_logistics: { palette: 'slate', visualPattern: 'customs_docs' },
  cold_chain: { palette: 'teal', visualPattern: 'cold_chain' },
  project_cargo: { palette: 'slate', visualPattern: 'control_tower' },
  total_logistics: { palette: 'signal_blue', visualPattern: 'control_tower' },
};

function buildAiStorefrontDraft(form: FormState, brief: string): Pick<FormState, 'site' | 'home'> {
  const industry = INDUSTRY_OPTIONS.find((option) => option.value === form.visual.industryTemplate);
  const toneLabel = TONE_OPTIONS.find((option) => option.value === form.visual.tone)?.label ?? 'Corporate';
  const serviceWords = [
    form.modules.tracking ? 'tracking' : '',
    form.modules.bookings ? 'booking' : '',
    form.modules.salesInquiry ? 'rate inquiry' : '',
    form.modules.portalLogin ? 'customer portal' : '',
  ].filter(Boolean).join(', ');
  const brand = form.site.brandShortName || form.site.brandName;
  const work = industry?.hint ?? 'logistics operations';
  const humanBrief = brief.trim();

  return {
    site: {
      ...form.site,
      tagline: humanBrief
        ? `${toneLabel} ${industry?.label ?? 'logistics'} service portal for ${humanBrief}.`
        : `${toneLabel} customer portal for ${work}`,
      footerBlurb: `${brand} customer storefront for ${work.toLowerCase()}${serviceWords ? `, with ${serviceWords}` : ''}.`,
    },
    home: {
      ...form.home,
      heroBadge: `${industry?.label ?? 'Logistics'} · ${toneLabel}`,
      heroTitle: brand,
      heroAccent: form.visual.tone === 'operations_first' ? 'Live Customer Operations' : 'Customer Service Portal',
      heroDescription: humanBrief
        ? `Access ${brand} services for ${humanBrief}. Track jobs, submit requests, and keep documents moving from one branded portal.`
        : `Track, request, and manage ${industry?.label.toLowerCase() ?? 'logistics'} work with ${brand}.`,
      ctaTitle: `Work with ${brand} online`,
      ctaDescription: `Start with tracking, booking, RFQ or portal login. The page reflects the services ${brand} actually provides.`,
    },
  };
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-4 rounded-xl border bg-card p-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-4 w-4 rounded border-input text-primary focus:ring-primary/30"
      />
    </label>
  );
}

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="space-y-1">
      <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">{title}</h2>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function StorefrontPreview({ form, mode }: { form: FormState; mode: 'desktop' | 'mobile' }) {
  const palette = resolveThemePalette(form.visual.palette);
  const enabledActions = [
    form.modules.tracking ? 'Track' : '',
    form.modules.bookings ? 'Book' : '',
    form.modules.salesInquiry ? 'RFQ' : '',
    form.modules.portalLogin ? form.site.portalLabel : '',
  ].filter(Boolean);
  const frameClass = mode === 'mobile' ? 'mx-auto max-w-[280px]' : 'w-full';

  return (
    <div className={frameClass}>
      <div
        className="overflow-hidden rounded-2xl border shadow-sm"
        style={{ borderColor: `${palette.motifMuted}80`, background: palette.surface }}
      >
        <div className="relative min-h-[260px] overflow-hidden text-white">
          <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${palette.surfaceStrong}, ${palette.motifMain})` }} />
          <ThemePatternBackground
            pattern={form.visual.visualPattern}
            palette={form.visual.palette}
            intensity="medium"
            tone="onDark"
            className="absolute inset-0 h-full w-full opacity-50"
          />
          {form.visual.mediaStrategy === 'tenant_upload' && form.visual.heroMediaUrl && (
            <img
              src={form.visual.heroMediaUrl}
              alt={form.visual.heroMediaAlt || `${form.site.brandShortName} operations`}
              className="absolute inset-0 h-full w-full object-cover opacity-45"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
          <div className="relative p-5">
            <div className="mb-6 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold leading-tight">{form.site.brandShortName || form.site.brandName}</p>
                <p className="text-[11px] text-white/70">{form.site.portalLabel}</p>
              </div>
              <div className="rounded-full bg-white/15 px-2 py-1 text-[10px] font-semibold">
                {form.visual.tone}
              </div>
            </div>
            <div className={mode === 'mobile' ? 'max-w-[220px]' : 'max-w-[420px]'}>
              <p className="mb-2 inline-flex rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]">
                {form.home.heroBadge}
              </p>
              <h3 className={mode === 'mobile' ? 'text-2xl font-bold leading-tight' : 'text-3xl font-bold leading-tight'}>
                {form.home.heroTitle || form.site.brandName}
                <span className="block" style={{ color: palette.motifMuted }}>{form.home.heroAccent}</span>
              </h3>
              <p className="mt-3 text-xs leading-5 text-white/78">{form.home.heroDescription}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {enabledActions.slice(0, mode === 'mobile' ? 2 : 4).map((action, index) => (
                  <span
                    key={action}
                    className={`rounded-lg px-3 py-1.5 text-[11px] font-semibold ${index === 0 ? 'bg-white text-slate-900' : 'bg-white/15 text-white'}`}
                  >
                    {action}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={`grid gap-2 p-4 ${mode === 'mobile' ? 'grid-cols-1' : 'grid-cols-3'}`}>
          {enabledActions.slice(0, 3).map((action) => (
            <div key={action} className="rounded-xl border bg-white p-3" style={{ borderColor: `${palette.motifMuted}70` }}>
              <p className="text-xs font-semibold" style={{ color: palette.text }}>{action}</p>
              <p className="mt-1 text-[11px] text-slate-500">Tenant-branded service module</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SettingsStorefrontPage() {
  const { user, loading: authLoading } = useAuth();
  const canManage = ['admin', 'manager', 'platform_admin'].includes(user?.role ?? '');
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [aiBrief, setAiBrief] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  useEffect(() => {
    if (authLoading) return;
    if (!canManage) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    apiFetch<StorefrontLitePayload>('/storefront-lite')
      .then((payload) => {
        if (cancelled) return;
        setForm({ ...payload, visual: { ...DEFAULT_VISUAL_PROFILE, ...(payload.visual ?? {}) } });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load storefront settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, canManage]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await apiFetch('/storefront-lite', {
        method: 'PATCH',
        body: JSON.stringify({
          site: form.site,
          home: form.home,
          visual: form.visual,
          pages: form.pages,
          modules: form.modules,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save storefront settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDraftCopy = async () => {
    if (!form) return;
    setDrafting(true);
    setError('');
    try {
      const draft = await apiFetch<StorefrontDraftResponse>('/storefront-lite/draft', {
        method: 'POST',
        body: JSON.stringify({
          brief: aiBrief,
          current: {
            site: form.site,
            home: form.home,
            visual: form.visual,
            pages: form.pages,
            modules: form.modules,
          },
        }),
      });
      update('site', draft.site);
      update('home', draft.home);
    } catch (err) {
      const draft = buildAiStorefrontDraft(form, aiBrief);
      update('site', draft.site);
      update('home', draft.home);
      setError(err instanceof Error ? `Used local draft fallback: ${err.message}` : 'Used local draft fallback');
    } finally {
      setDrafting(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl py-16 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="mx-auto max-w-3xl py-10 px-4">
        <div className="rounded-2xl border bg-card p-6">
          <div className="flex items-center gap-3 mb-3">
            <ShieldAlert className="h-5 w-5 text-amber-500" />
            <h1 className="text-lg font-semibold">Storefront Lite</h1>
          </div>
          <p className="text-sm text-muted-foreground">Only admin or manager users can manage storefront settings.</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="mx-auto max-w-3xl py-10 px-4">
        <div className="rounded-2xl border bg-card p-6 space-y-3">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-amber-500" />
            <h1 className="text-lg font-semibold">Storefront Lite unavailable</h1>
          </div>
          <p className="text-sm text-muted-foreground">{error || 'Self-service feature is not enabled for this tenant.'}</p>
          <Link to="/settings/company" className="inline-flex text-sm font-medium text-primary hover:underline">
            Back to Company Settings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 py-6 px-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-bold">Storefront Lite</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Standard pages and online-service switches for your `boox.network` frontpage. This is not a website builder.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <a href={form.storefrontUrl} target="_blank" rel="noreferrer" className="rounded-full border px-3 py-1.5 text-foreground hover:bg-muted">
            <Globe className="mr-1 inline h-3.5 w-3.5" />
            {form.storefrontHost}
          </a>
          <a href={form.portalUrl} target="_blank" rel="noreferrer" className="rounded-full border px-3 py-1.5 text-foreground hover:bg-muted">
            <Sparkles className="mr-1 inline h-3.5 w-3.5" />
            {form.portalHost}
          </a>
        </div>
      </div>

      <section className="rounded-2xl border bg-card p-5 space-y-4">
        <SectionHeader
          title="Brand Copy"
          description="Keep the structure standard. Only update public-facing copy and portal naming."
        />
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium">Brand Name</label>
            <input value={form.site.brandName} onChange={(e) => update('site', { ...form.site, brandName: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Short Name</label>
            <input value={form.site.brandShortName} onChange={(e) => update('site', { ...form.site, brandShortName: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Legal Name</label>
            <input value={form.site.legalName} onChange={(e) => update('site', { ...form.site, legalName: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Portal Label</label>
            <input value={form.site.portalLabel} onChange={(e) => update('site', { ...form.site, portalLabel: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Tagline</label>
          <input value={form.site.tagline} onChange={(e) => update('site', { ...form.site, tagline: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Footer Blurb</label>
          <textarea value={form.site.footerBlurb} onChange={(e) => update('site', { ...form.site, footerBlurb: e.target.value })} rows={3} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
        </div>
      </section>

      <section className="rounded-2xl border bg-card p-5 space-y-4">
        <SectionHeader
          title="Theme Builder"
          description="Make the storefront tenant-branded and industry-aware. The picture and tone should reflect the work this company actually does."
        />
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-sm font-medium">Industry Template</span>
            <select
              value={form.visual.industryTemplate}
              onChange={(event) => {
                const industryTemplate = event.target.value as StorefrontIndustryTemplate;
                update('visual', {
                  ...form.visual,
                  industryTemplate,
                  ...INDUSTRY_VISUAL_DEFAULTS[industryTemplate],
                });
              }}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            >
              {INDUSTRY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <span className="block text-xs text-muted-foreground">
              {INDUSTRY_OPTIONS.find((option) => option.value === form.visual.industryTemplate)?.hint}
            </span>
          </label>

          <label className="space-y-1.5">
            <span className="text-sm font-medium">Tone</span>
            <select
              value={form.visual.tone}
              onChange={(event) => update('visual', { ...form.visual, tone: event.target.value as StorefrontTone })}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            >
              {TONE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <span className="block text-xs text-muted-foreground">
              Controls whether the page feels corporate, friendly, premium, or operational.
            </span>
          </label>

          <label className="space-y-1.5">
            <span className="text-sm font-medium">Theme Color</span>
            <select
              value={form.visual.palette}
              onChange={(event) => update('visual', { ...form.visual, palette: event.target.value as ThemePaletteId })}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            >
              {THEME_PALETTE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <span className="block text-xs text-muted-foreground">
              {THEME_PALETTE_OPTIONS.find((option) => option.value === form.visual.palette)?.description}
            </span>
          </label>

          <label className="space-y-1.5">
            <span className="text-sm font-medium">Visual Pattern</span>
            <select
              value={form.visual.visualPattern}
              onChange={(event) => update('visual', { ...form.visual, visualPattern: event.target.value as VisualPatternId })}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            >
              {VISUAL_PATTERN_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <span className="block text-xs text-muted-foreground">
              {VISUAL_PATTERN_OPTIONS.find((option) => option.value === form.visual.visualPattern)?.description}
            </span>
          </label>

          <label className="space-y-1.5">
            <span className="text-sm font-medium">Media Strategy</span>
            <select
              value={form.visual.mediaStrategy}
              onChange={(event) => update('visual', { ...form.visual, mediaStrategy: event.target.value as StorefrontMediaStrategy })}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            >
              {MEDIA_STRATEGY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <span className="block text-xs text-muted-foreground">
              Company image URL wins first. Industry fallback is used when no approved tenant image exists.
            </span>
          </label>

          <label className="space-y-1.5">
            <span className="text-sm font-medium">Hero Image URL</span>
            <input
              value={form.visual.heroMediaUrl}
              onChange={(event) => update('visual', { ...form.visual, heroMediaUrl: event.target.value })}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
              placeholder="https://..."
            />
            <span className="block text-xs text-muted-foreground">
              Use a real company-approved photo: vehicle, warehouse, yard, depot, ISO tank, cargo, or team operation.
            </span>
          </label>
        </div>

        <label className="space-y-1.5 block">
          <span className="text-sm font-medium">Hero Image Alt Text</span>
          <input
            value={form.visual.heroMediaAlt}
            onChange={(event) => update('visual', { ...form.visual, heroMediaAlt: event.target.value })}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            placeholder="Prime mover collecting container at port gate"
          />
        </label>

        <div className="rounded-xl border p-4">
          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">Live Theme Preview</p>
              <p className="text-xs text-muted-foreground">
                This reflects industry, color, pattern, media strategy, copy and enabled service modules.
              </p>
            </div>
            <div className="inline-flex rounded-lg border bg-background p-1">
              <button
                type="button"
                onClick={() => setPreviewMode('desktop')}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${previewMode === 'desktop' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Monitor className="h-3.5 w-3.5" />
                Desktop
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode('mobile')}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${previewMode === 'mobile' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Smartphone className="h-3.5 w-3.5" />
                Mobile
              </button>
            </div>
          </div>
          <StorefrontPreview form={form} mode={previewMode} />
        </div>

        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="mb-3 flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" />
            <div>
              <p className="text-sm font-semibold text-foreground">AI Content Assistant Draft</p>
              <p className="text-xs text-muted-foreground">
                Write plain language about the company. The assistant turns it into matching hero and company copy for the builder.
              </p>
            </div>
          </div>
          <textarea
            value={aiBrief}
            onChange={(event) => setAiBrief(event.target.value)}
            rows={3}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            placeholder="Example: We are a Singapore container haulier doing PSA import/export, depot moves and customer delivery updates..."
          />
          <button
            type="button"
            onClick={handleDraftCopy}
            disabled={drafting}
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {drafting ? 'Drafting...' : 'Draft Copy Into Builder'}
          </button>
        </div>

        <div className="rounded-xl border bg-muted/20 p-4">
          <div className="mb-3 flex items-center gap-2">
            <Image className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold">Preview Logic</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Runtime will use the company image URL first. If blank, it falls back to an industry-specific image for {INDUSTRY_OPTIONS.find((option) => option.value === form.visual.industryTemplate)?.label}.
            Public modules below stay feature-complete and control which action buttons appear.
          </p>
        </div>
      </section>

      <section className="rounded-2xl border bg-card p-5 space-y-4">
        <SectionHeader
          title="Homepage Copy"
          description="Edit the standard homepage hero and main CTA. Page layout stays fixed."
        />
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium">Hero Badge</label>
            <input value={form.home.heroBadge} onChange={(e) => update('home', { ...form.home, heroBadge: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Hero Title</label>
            <input value={form.home.heroTitle} onChange={(e) => update('home', { ...form.home, heroTitle: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Hero Accent</label>
            <input value={form.home.heroAccent} onChange={(e) => update('home', { ...form.home, heroAccent: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">CTA Title</label>
            <input value={form.home.ctaTitle} onChange={(e) => update('home', { ...form.home, ctaTitle: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Hero Description</label>
          <textarea value={form.home.heroDescription} onChange={(e) => update('home', { ...form.home, heroDescription: e.target.value })} rows={3} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">CTA Description</label>
          <textarea value={form.home.ctaDescription} onChange={(e) => update('home', { ...form.home, ctaDescription: e.target.value })} rows={3} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
        </div>
      </section>

      <section className="rounded-2xl border bg-card p-5 space-y-4">
        <SectionHeader
          title="Standard Pages"
          description="Turn standard pages on or off. Users can change copy, but not redesign page structure."
        />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3 rounded-xl border p-4">
            <ToggleRow
              label="Services Page"
              description="Public service introduction page."
              checked={form.pages.services.enabled}
              onChange={(enabled) => update('pages', { ...form.pages, services: { ...form.pages.services, enabled } })}
            />
            <div>
              <label className="mb-1 block text-sm font-medium">Title</label>
              <input value={form.pages.services.title} onChange={(e) => update('pages', { ...form.pages, services: { ...form.pages.services, title: e.target.value } })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Intro</label>
              <textarea value={form.pages.services.intro} onChange={(e) => update('pages', { ...form.pages, services: { ...form.pages.services, intro: e.target.value } })} rows={4} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="space-y-3 rounded-xl border p-4">
            <ToggleRow
              label="Contact Page"
              description="Public company contact page."
              checked={form.pages.contact.enabled}
              onChange={(enabled) => update('pages', { ...form.pages, contact: { ...form.pages.contact, enabled } })}
            />
            <div>
              <label className="mb-1 block text-sm font-medium">Title</label>
              <input value={form.pages.contact.title} onChange={(e) => update('pages', { ...form.pages, contact: { ...form.pages.contact, title: e.target.value } })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Intro</label>
              <textarea value={form.pages.contact.intro} onChange={(e) => update('pages', { ...form.pages, contact: { ...form.pages.contact, intro: e.target.value } })} rows={4} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border bg-card p-5 space-y-4">
        <SectionHeader
          title="Online Services"
          description="These are the sellable online-service modules surfaced on the standard storefront."
        />
        <div className="grid gap-3 md:grid-cols-2">
          <ToggleRow label="Tracking" description="Public shipment tracking page and home quick action." checked={form.modules.tracking} onChange={(tracking) => update('modules', { ...form.modules, tracking })} />
          <ToggleRow label="Sailing Schedule" description="Public sailing schedule search page." checked={form.modules.sailingSchedule} onChange={(sailingSchedule) => update('modules', { ...form.modules, sailingSchedule })} />
          <ToggleRow label="Booking Request" description="Online booking request CTA and page." checked={form.modules.bookings} onChange={(bookings) => update('modules', { ...form.modules, bookings })} />
          <ToggleRow label="Sales Inquiry / RFQ" description="Public sales inquiry or quote request CTA." checked={form.modules.salesInquiry} onChange={(salesInquiry) => update('modules', { ...form.modules, salesInquiry })} />
          <ToggleRow label="Other Inquiry" description="General inquiry form for non-booking questions." checked={form.modules.otherInquiry} onChange={(otherInquiry) => update('modules', { ...form.modules, otherInquiry })} />
          <ToggleRow label="Customer Login" description="Portal login and customer workspace access." checked={form.modules.portalLogin} onChange={(portalLogin) => update('modules', { ...form.modules, portalLogin })} />
          <ToggleRow label="AI Chat" description="Floating AI assistant on the storefront." checked={form.modules.aiChat} onChange={(aiChat) => update('modules', { ...form.modules, aiChat })} />
        </div>
      </section>

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <Link to="/settings/company" className="text-sm text-muted-foreground hover:text-foreground">
          Back to Company Settings
        </Link>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saved ? 'Saved' : 'Save Storefront'}
        </button>
      </div>
    </div>
  );
}
